import { useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import { setDealLoadStatus } from 'cache/deal-load-status'
import {
  DealLoadStatusRequestInput,
  DealLoadStatusResponse,
  GET_DEAL_LOAD_STATUS,
} from 'queries/get-deal-load-status'

/**
 * Options for configuring the behavior of the `useDealLoadStatus` hook.
 * @property {boolean} [skipSideEffects] - When set to `true`, skips the execution of side effects. Defaults to `false`.
 */
interface UseDealLoadStatusOptions {
  skipSideEffects?: boolean
}

export const useDealLoadStatus = ({ skipSideEffects = false }: UseDealLoadStatusOptions = {}) => {
  const [getDealLoadStatus, { data, loading, error }] = useLazyQuery<
    DealLoadStatusResponse,
    DealLoadStatusRequestInput
  >(GET_DEAL_LOAD_STATUS, {
    fetchPolicy: 'no-cache',
    onCompleted: (responseData) => {
      if (!skipSideEffects && responseData?.dealLoadStatus) {
        setDealLoadStatus(responseData.dealLoadStatus)
      }
    },
    onError: () => {
      if (!skipSideEffects) setDealLoadStatus(null)
    },
  })

  const fetchDealLoadStatus = useCallback(
    async (dealId: number, userId: string) => {
      return getDealLoadStatus({
        variables: {
          request: {
            dealId,
            userId,
          },
        },
      })
    },
    [getDealLoadStatus]
  )

  return {
    fetchDealLoadStatus,
    data: data?.dealLoadStatus,
    loading,
    error,
  }
}
